/** Saved opening/closing inventory, adjusted to the period edges. See spec-inventory-group-types.md, phase 2. */
import type { OrderHistory } from '../stores/appOrderHistoryStore';
import type { RevenueHistory } from '../services/revenuehistory/revenueHistoryService';
import type { ExpectedInventoryBootstrap } from '../services/inventory/expectedInventoryService';
import {
  ExpectedSession, expectedDayWeight, expectedInventoryDate, groupSavedSessions, overlapsFor, resolveExpectedCountDayOrders,
} from './reports/expectedInventory';
import { inventoryKey } from './reports/consumptionReport';
import { baseProductKey } from './inventoryProductKey';
import { calculateProductTotal } from './platformUtils';
import { filterRevenueByOverallType } from '../components/revenuehistory/utils';
import { calculateTotalRevenue } from './revenueHistoryCalculations';

export type StockSide = 'food' | 'drinks';
const SIDES: StockSide[] = ['food', 'drinks'];
/** A count this close to an edge is used as-is. */
export const EXACT_COUNT_DAYS = 1;
/** Further than this from every count hides the side. */
export const MAX_COUNT_DISTANCE_DAYS = 4;
/** The count on the other side of an edge, used to measure usage, must be within this. */
export const MAX_BRACKET_DAYS = 31;

export interface SideStockAdjustment {
  /** Counted values at the chosen opening/closing counts. */
  stockStart: number;
  stockEnd: number;
  /** Correction of the stock change from the count dates to the period edges (linked products only). */
  adjustment: number;
  /** Some count is more than EXACT_COUNT_DAYS from its edge. */
  estimated: boolean;
  startCountDays: string[];
  endCountDays: string[];
}
export type StockAdjustment = Record<StockSide, SideStockAdjustment | null>;
export interface StockAdjustmentInputs {
  restaurantId: string;
  bootstrap: ExpectedInventoryBootstrap;
  /** The restaurant's complete order history (store read path: factuur + price adjusted). */
  orders: OrderHistory[];
  /** The restaurant's complete revenue history. */
  revenue: RevenueHistory[];
  /** First and last business day of the period; end is inclusive. */
  startDay: string;
  endDay: string;
  showExVatOrders: boolean;
  now: number;
}

const DAY_MS = 86400000;
const dayNumber = (day: string) => Date.UTC(+day.slice(0, 4), +day.slice(5, 7) - 1, +day.slice(8, 10)) / DAY_MS;
const amsterdamHour = new Intl.DateTimeFormat('en-GB', { timeZone: 'Europe/Amsterdam', hour: '2-digit', hourCycle: 'h23' });
/** 00:00 Europe/Amsterdam of business day `day`, as epoch ms (DST-safe, browser-timezone independent). */
const amsterdamMidnight = (day: string) => {
  const utcMidnight = dayNumber(day) * DAY_MS;
  return utcMidnight - Number(amsterdamHour.format(new Date(utcMidnight))) * 3600000;
};
export const nextDay = (day: string) => new Date((dayNumber(day) + 1) * DAY_MS).toISOString().slice(0, 10);
/** Dashboard date pickers represent local calendar days. Count dates use Amsterdam. */
export const localDay = (date: Date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
const distance = (session: ExpectedSession, edge: string) => Math.abs(dayNumber(session.day) - dayNumber(edge));
const isPrepOrCustom = (key: string) => key.startsWith('subrecipe:') || key.startsWith('custom:');

/** Calendar proximity first; ties prefer the earlier day, then the latest saved session that day. */
function closestCount(sessions: ExpectedSession[], edge: string): ExpectedSession | undefined {
  return sessions.filter(session => distance(session, edge) <= MAX_COUNT_DISTANCE_DAYS)
    .sort((a, b) => distance(a, edge) - distance(b, edge) || a.day.localeCompare(b.day) || b.at - a.at || a.key.localeCompare(b.key))[0];
}

interface PurchaseLine { orderId: string; key: string; day: string; amount: number }

export function computeStockAdjustment(inputs: StockAdjustmentInputs): StockAdjustment {
  const { bootstrap, restaurantId, startDay, endDay, now, showExVatOrders } = inputs;
  const result: StockAdjustment = { food: null, drinks: null };
  const today = expectedInventoryDate(now);
  if (bootstrap.restaurantId !== restaurantId || endDay >= today || startDay > endDay) return result;
  const endEdge = nextDay(endDay);
  const groups = bootstrap.groups.filter(group => group.restaurantId === restaurantId);
  const sections = bootstrap.sections.filter(section => section.restaurantId === restaurantId);
  const sectionGroups = new Map(sections.map(section => [section.id, section.groupId]));
  const sessions = groupSavedSessions({ countsHistory: bootstrap.counts, sections, groups, currentRestaurantId: restaurantId, now });
  const configured = new Map<string, Set<string>>();
  for (const product of bootstrap.products) {
    const groupId = sectionGroups.get(product.inventorySectionId);
    if (product.restaurantId !== restaurantId || !groupId) continue;
    // A newly created assignment must not make a historical count incomplete.
    if (dayNumber(expectedInventoryDate(product.createdAt)) > dayNumber(endEdge) + MAX_COUNT_DISTANCE_DAYS) continue;
    configured.set(groupId, (configured.get(groupId) ?? new Set()).add(baseProductKey(product.productKey)));
  }
  const groupSessions = new Map<string, ExpectedSession[]>();
  const allGroupSessions = new Map<string, ExpectedSession[]>();
  for (const session of sessions) {
    allGroupSessions.set(session.groupId, [...(allGroupSessions.get(session.groupId) ?? []), session]);
    if (dayNumber(session.day) > dayNumber(endEdge) + MAX_COUNT_DISTANCE_DAYS) continue;
    groupSessions.set(session.groupId, [...(groupSessions.get(session.groupId) ?? []), session]);
    // Frozen products outlive assignments. Keep them in overlap/coverage decisions.
    const keys = configured.get(session.groupId) ?? new Set<string>();
    session.products.forEach((_, key) => keys.add(key));
    configured.set(session.groupId, keys);
  }
  const excluded = new Map<string, Set<string>>();
  const invalid = new Set<StockSide>();
  for (const relation of overlapsFor({ groups, settings: bootstrap.settings }, configured, Array.from(groupSessions.values()).flat())) {
    if (relation.mode !== 'included') continue; // Separate physical stock: sum its actual count, without a purchase split.
    const child = groups.find(group => group.id === relation.groupId)!;
    const parent = groups.find(group => group.id === relation.fullCountGroupId)!;
    if (child.groupType !== parent.groupType) {
      invalid.add(child.groupType); invalid.add(parent.groupType);
      continue;
    }
    const keys = excluded.get(child.id) ?? new Set<string>();
    configured.get(child.id)?.forEach(key => { if (configured.get(parent.id)?.has(key)) keys.add(key); });
    excluded.set(child.id, keys);
  }
  // A product held by two counted groups (separate stock) has no single owner for its purchases.
  const owners = new Map<string, number>();
  configured.forEach((keys, groupId) => keys.forEach(key => {
    if (!excluded.get(groupId)?.has(key)) owners.set(key, (owners.get(key) ?? 0) + 1);
  }));

  // --- purchases of order-history-linked products and side revenue, for edge adjustments ---------
  const lines: PurchaseLine[] = [];
  const orders = inputs.orders.filter(order => (!order.restaurantId || order.restaurantId === restaurantId) &&
    Number.isFinite(new Date(order.orderDate).getTime()));
  for (const order of orders) {
    const day = expectedInventoryDate(order.orderDate);
    order.productData.forEach(line => {
      const key = inventoryKey(order.platform, line.articleId, line.name);
      if (key) lines.push({ orderId: order.id, key, day, amount: calculateProductTotal(line, order.platform, showExVatOrders) });
    });
  }
  const linkedKeys = new Set(lines.map(line => line.key));
  const revenue = inputs.revenue.filter(row => (!row.restaurantId || row.restaurantId === restaurantId) &&
    Number.isFinite(new Date(row.date).getTime()));
  const dailyRevenue: Record<StockSide, Map<string, number>> = { food: new Map(), drinks: new Map() };
  for (const side of SIDES) {
    for (const row of filterRevenueByOverallType(revenue, side)) {
      const day = expectedInventoryDate(row.date);
      dailyRevenue[side].set(day, (dailyRevenue[side].get(day) ?? 0) + calculateTotalRevenue([row], true));
    }
  }
  const countedIds = new Map<ExpectedSession, Set<string>>();
  const countedFor = (session: ExpectedSession) => {
    let ids = countedIds.get(session);
    if (!ids) { ids = new Set(resolveExpectedCountDayOrders(session, orders, bootstrap.settings).countedOrderIds); countedIds.set(session, ids); }
    return ids;
  };
  /** Purchases after `from`'s count, up to edge day `edge` (exclusive) or through count `to`. */
  const purchasesBetween = (keys: Set<string>, from: ExpectedSession, to: { edge: string } | { session: ExpectedSession }) => {
    let sum = 0;
    for (const line of lines) {
      if (!keys.has(line.key) || line.day < from.day) continue;
      if (line.day === from.day && countedFor(from).has(line.orderId)) continue;
      if ('edge' in to ? line.day >= to.edge : line.day > to.session.day ||
        (line.day === to.session.day && !countedFor(to.session).has(line.orderId))) continue;
      sum += line.amount;
    }
    return sum;
  };
  /** Share of the c0→c1 window's usage that falls before `edge`: by side revenue, else elapsed time. */
  const usageShare = (side: StockSide, c0: ExpectedSession, c1: ExpectedSession, edge: string) => {
    let part = 0, total = 0, negative = false;
    dailyRevenue[side].forEach((amount, day) => {
      const weight = expectedDayWeight(c0.at, c1.at, day);
      if (!weight) return;
      if (amount < 0) negative = true;
      total += weight * amount;
      if (day < edge) part += weight * amount;
    });
    const share = total > 0 && !negative ? part / total : (amsterdamMidnight(edge) - c0.at) / Math.max(1, c1.at - c0.at);
    return Math.min(1, Math.max(0, share));
  };

  for (const group of groups) {
    const side = group.groupType;
    if (!SIDES.includes(side)) { invalid.add('food'); invalid.add('drinks'); continue; }
    const counts = groupSessions.get(group.id) ?? [];
    const keys = configured.get(group.id) ?? new Set<string>();
    const omitted = excluded.get(group.id) ?? new Set<string>();
    if (!counts.length && !keys.size) continue; // Empty/new inventories do not invalidate historical periods.
    if (omitted.size && Array.from(keys).every(key => omitted.has(key))) continue; // Entirely covered by a larger inventory.
    const opening = closestCount(counts, startDay);
    const closing = closestCount(counts, endEdge);
    if (!opening || !closing || closing.at <= opening.at) { invalid.add(side); continue; }
    const ratio = (key: string) => showExVatOrders ? 1 : bootstrap.stockVatRatios?.[key] ?? 1;
    const value = (session: ExpectedSession, only?: Set<string>) => {
      let total = 0;
      session.products.forEach((product, key) => {
        if (omitted.has(key) || (only && !only.has(key))) return;
        // Historical stock stays frozen. VAT metadata only changes the display basis.
        total += product.value * ratio(key);
      });
      return total;
    };
    // Only order-history products owned by this group move to the edge; everything else stays as counted.
    const adjustable = new Set(Array.from(keys).filter(key => !omitted.has(key) && !isPrepOrCustom(key) &&
      linkedKeys.has(key) && owners.get(key) === 1));
    const all = allGroupSessions.get(group.id) ?? [];
    /** Linked stock moved from `nearest` to the edge; 0 when exact, null when unmeasurable. */
    const edgeAdjustment = (nearest: ExpectedSession, edge: string): number | null => {
      if (distance(nearest, edge) <= EXACT_COUNT_DAYS || !adjustable.size) return 0;
      const c0 = all.filter(s => s.day < edge && dayNumber(edge) - dayNumber(s.day) <= MAX_BRACKET_DAYS).sort((a, b) => b.at - a.at)[0];
      const c1 = all.filter(s => s.day > edge && dayNumber(s.day) - dayNumber(edge) <= MAX_BRACKET_DAYS).sort((a, b) => a.at - b.at)[0];
      // Usage needs every sale up to the later count; today's sales are not complete yet.
      if (!c0 || !c1 || c1.day >= today) return null;
      const v0 = value(c0, adjustable);
      const usage = v0 + purchasesBetween(adjustable, c0, { session: c1 }) - value(c1, adjustable);
      const atEdge = v0 + purchasesBetween(adjustable, c0, { edge }) - usage * usageShare(side, c0, c1, edge);
      return atEdge - value(nearest, adjustable);
    };
    const startAdjustment = edgeAdjustment(opening, startDay);
    const endAdjustment = edgeAdjustment(closing, endEdge);
    const start = value(opening), end = value(closing);
    if (startAdjustment === null || endAdjustment === null || ![start, end, startAdjustment, endAdjustment].every(Number.isFinite)) {
      invalid.add(side); continue;
    }
    const stock = result[side] ?? { stockStart: 0, stockEnd: 0, adjustment: 0, estimated: false, startCountDays: [], endCountDays: [] };
    stock.stockStart += start;
    stock.stockEnd += end;
    stock.adjustment += endAdjustment - startAdjustment;
    stock.estimated ||= distance(opening, startDay) > EXACT_COUNT_DAYS || distance(closing, endEdge) > EXACT_COUNT_DAYS;
    stock.startCountDays.push(opening.day);
    stock.endCountDays.push(closing.day);
    result[side] = stock;
  }
  for (const side of SIDES) {
    const stock = result[side];
    if (invalid.has(side)) result[side] = null;
    else if (stock) {
      stock.startCountDays = Array.from(new Set(stock.startCountDays)).sort();
      stock.endCountDays = Array.from(new Set(stock.endCountDays)).sort();
    }
  }
  return result;
}

/** Stock change at the period edges: counted change plus the edge adjustment. */
export const stockChange = (stock: SideStockAdjustment) => stock.stockEnd - stock.stockStart + stock.adjustment;

export function stockAdjustedCostPercent(purchases: number, revenue: number, stock: SideStockAdjustment): number | null {
  if (!(revenue > 0) || !Number.isFinite(revenue)) return null;
  const percent = ((purchases - stockChange(stock)) / revenue) * 100;
  return Number.isFinite(percent) ? percent : null;
}
